<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { transformYearlyFortuneText } from '@/composables/splitSentence';
import ContentsTitle from './ContentsTitle.vue';

const props = defineProps({
  userName: { type: String, default: '' },
  isMobile: { type: Boolean, default: false },
  data: { type: Object, default: () => ({}) },
});

// 데이터 접근
const 운세흐름 = computed(() => props.data?.운세흐름 || {});
const 개념특징 = computed(() => 운세흐름.value?.개념특징 || {});
const 주요강점 = computed(() => 운세흐름.value?.주요강점 || {});
const 주요약점 = computed(() => 운세흐름.value?.주요약점 || {});

// 버블 데이터 (문자열을 ', ' 기준으로 분리)
const 특징요약 = computed(() => {
  const raw = 개념특징.value?.특징요약 || '';
  return raw ? raw.split(', ') : [];
}); // 파란색
const 강점요약 = computed(() => {
  const raw = 주요강점.value?.강점요약 || '';
  return raw ? raw.split(', ') : [];
}); // 빨간색
const 약점요약 = computed(() => {
  const raw = 주요약점.value?.약점요약 || '';
  return raw ? raw.split(', ') : [];
}); // 노란색

// 경험 카드 데이터
const 특징설명 = computed(() => transformYearlyFortuneText(개념특징.value?.개념 || ''));
const 특징상세 = computed(() => {
  const raw = 개념특징.value?.특징 || '';
  return raw ? raw.split('| ') : [];
});

// 오른쪽 컬럼 데이터 (| 기준 줄바꿈)
const 강점설명 = computed(() => transformYearlyFortuneText(주요강점.value?.강점설명 || ''));
const 강점추천 = computed(() => transformYearlyFortuneText(주요강점.value?.강점추천 || ''));
const 약점설명 = computed(() => transformYearlyFortuneText(주요약점.value?.약점설명 || ''));
const 약점추천 = computed(() => transformYearlyFortuneText(주요약점.value?.약점추천 || ''));

// 버블 위치 클래스 (파란색 - 특징)
const blueBubbleClasses = ['blue-first', 'blue-second', 'blue-third'];
// 버블 위치 클래스 (빨간색 - 강점)
const redBubbleClasses = ['red-first', 'red-second', 'red-third'];
// 버블 위치 클래스 (노란색 - 약점)
const yellowBubbleClasses = ['yellow-first', 'yellow-second', 'yellow-third'];
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <!-- 타이틀 -->
        <ContentsTitle
          :number="2"
          text="2026년을 이해하는데 꼭 필요한 나만의 핵심 흐름?"
          :isMobile="isMobile"
        />

        <div class="flow-wrapper" :class="{ mobile: isMobile }">
          <!-- 버블 차트 영역 -->
          <div class="bubble-chart" :class="{ mobile: isMobile }">
            <!-- 가운데 원 -->
            <div class="bubble-chart__center">
              <span>丙午年</span>
            </div>

            <!-- 파란색 버블 (특징요약) -->
            <div
              v-for="(item, idx) in 특징요약.slice(0, 3)"
              :key="`blue-${idx}`"
              class="bubble-chart__bubble bubble--blue"
              :class="blueBubbleClasses[idx]"
            >
              <span>{{ item }}</span>
            </div>

            <!-- 빨간색 버블 (강점요약) -->
            <div
              v-for="(item, idx) in 강점요약.slice(0, 3)"
              :key="`red-${idx}`"
              class="bubble-chart__bubble bubble--red"
              :class="redBubbleClasses[idx]"
            >
              <span>{{ item }}</span>
            </div>

            <!-- 노란색 버블 (약점요약) -->
            <div
              v-for="(item, idx) in 약점요약.slice(0, 3)"
              :key="`yellow-${idx}`"
              class="bubble-chart__bubble bubble--yellow"
              :class="yellowBubbleClasses[idx]"
            >
              <span>{{ item }}</span>
            </div>
          </div>

          <!-- 경험 섹션 -->
          <section class="flow-section">
            <div class="flow-section__title">
              <span>👀</span>
              <span>올해 이런 경험을 마주할 수 있어요.</span>
            </div>

            <!-- 경험 카드 -->
            <div class="flow-card flow-card--blue">
              <!-- 키워드 태그 -->
              <div class="flow-card__tags">
                <span
                  v-for="(item, idx) in 특징요약.slice(0, 3)"
                  :key="`tag-${idx}`"
                  class="flow-card__tag"
                >
                  {{ item }}
                </span>
              </div>

              <!-- 설명 텍스트 -->
              <p class="flow-card__desc" v-html="특징설명"></p>

              <!-- 상세 카드들 -->
              <div class="flow-card__items">
                <div v-for="(item, idx) in 특징상세" :key="`detail-${idx}`" class="flow-card__item">
                  <p v-html="transformYearlyFortuneText(item)"></p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="flow-wrapper" :class="{ mobile: isMobile }">
          <!-- 살리면 좋은 방향 (강점) -->
          <section class="flow-section">
            <div class="flow-section__title">
              <span>💛</span>
              <span>살리면 좋은 방향</span>
            </div>

            <div class="flow-card flow-card--red">
              <div class="flow-card__content">
                <!-- 키워드 태그 -->
                <div class="flow-card__tags">
                  <span
                    v-for="(item, idx) in 강점요약.slice(0, 3)"
                    :key="`strength-tag-${idx}`"
                    class="flow-card__tag"
                  >
                    {{ item }}
                  </span>
                </div>

                <!-- 설명 텍스트 -->
                <p class="flow-card__desc" v-html="강점설명"></p>
              </div>

              <!-- 추천 카드 -->
              <div class="flow-card__recommend">
                <p class="flow-card__recommend-title">추천</p>
                <p class="flow-card__recommend-text" v-html="강점추천"></p>
              </div>
            </div>
          </section>

          <!-- 조율이 필요한 부분 (약점) -->
          <section class="flow-section">
            <div class="flow-section__title">
              <span>⚖</span>
              <span>조율이 필요한 부분</span>
            </div>

            <div class="flow-card flow-card--yellow">
              <div class="flow-card__content">
                <!-- 키워드 태그 -->
                <div class="flow-card__tags">
                  <span
                    v-for="(item, idx) in 약점요약.slice(0, 3)"
                    :key="`weakness-tag-${idx}`"
                    class="flow-card__tag"
                    v-html="transformYearlyFortuneText(item)"
                  >
                  </span>
                </div>

                <!-- 설명 텍스트 -->
                <p class="flow-card__desc" v-html="약점설명"></p>
              </div>

              <!-- 대응 카드 -->
              <div class="flow-card__recommend">
                <p class="flow-card__recommend-title">대응</p>
                <p class="flow-card__recommend-text" v-html="약점추천"></p>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
// 컨텐츠 래퍼
.flow-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  padding-bottom: 130px;
  gap: 24px;

  &.mobile {
    padding: 24px 16px;
    padding-bottom: 24px;
  }
}

// 버블 차트
.bubble-chart {
  position: relative;
  width: calc(100% + 48px);
  max-width: 420px;
  min-width: 320px;
  margin-left: -24px;
  margin-right: -24px;
  aspect-ratio: 420 / 240;

  &.mobile {
    width: calc(100% + 32px);
    max-width: 350px;
    margin-left: -16px;
    margin-right: -16px;
    aspect-ratio: 350 / 200;
  }

  &__center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 120px;
    height: 120px;
    border-radius: 60px;
    background: #ee5452;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      color: #fff;
      font-family: 'ChosunGs';
      font-size: 32px;
      font-weight: 400;
      line-height: 32px;
    }

    .bubble-chart.mobile & {
      width: 100px;
      height: 100px;

      span {
        font-size: 26px;
      }
    }
  }

  &__bubble {
    position: absolute;
    border-radius: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s ease;
    animation: bubblePop 0.4s ease-out backwards;

    &:hover {
      transform: scale(1.1);
    }

    span {
      color: #404040;
      font-family: Pretendard;
      font-weight: 700;
      text-align: center;
    }
  }
}

@keyframes bubblePop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// 파란색 버블 (특징)
.bubble--blue {
  background: rgba(87, 144, 252, 0.1);

  &.blue-first {
    animation-delay: 0.1s;
    width: 80px;
    height: 80px;
    left: 15%;
    top: 22%;

    span {
      font-size: 14px;
    }

    .bubble-chart.mobile & {
      width: 65px;
      height: 65px;
      span {
        font-size: 12px;
      }
    }
  }

  &.blue-second {
    animation-delay: 0.2s;
    width: 80px;
    height: 80px;
    left: 52%;
    top: 4%;

    span {
      font-size: 14px;
    }

    .bubble-chart.mobile & {
      width: 65px;
      height: 65px;
      left: 55%;
      span {
        font-size: 12px;
      }
    }
  }

  &.blue-third {
    animation-delay: 0.3s;
    width: 60px;
    height: 60px;
    left: 36%;
    top: 72%;

    span {
      font-size: 14px;
    }

    .bubble-chart.mobile & {
      width: 50px;
      height: 50px;
      span {
        font-size: 11px;
      }
    }
  }
}

// 빨간색 버블 (강점)
.bubble--red {
  background: rgba(255, 110, 110, 0.1);
  width: 100px;
  height: 100px;

  span {
    font-size: 15px;
  }

  .bubble-chart.mobile & {
    width: 80px;
    height: 80px;
    span {
      font-size: 13px;
    }
  }

  &.red-first {
    animation-delay: 0.15s;
    left: 62%;
    top: 24%;
  }

  &.red-second {
    animation-delay: 0.25s;
    left: 18%;
    top: 46%;
  }

  &.red-third {
    animation-delay: 0.35s;
    left: 60%;
    top: 61%;
    width: 60px;
    height: 60px;

    span {
      font-size: 12px;
    }

    .bubble-chart.mobile & {
      width: 50px;
      height: 50px;
      top: 58%;
      span {
        font-size: 11px;
      }
    }
  }
}

// 노란색 버블 (약점)
.bubble--yellow {
  background: rgba(243, 174, 17, 0.1);
  width: 60px;
  height: 60px;

  span {
    font-size: 12px;
  }

  .bubble-chart.mobile & {
    width: 50px;
    height: 50px;
    span {
      font-size: 11px;
    }
  }

  &.yellow-first {
    animation-delay: 0.2s;
    right: 12%;
    top: 5%;

    .bubble-chart.mobile & {
      right: 10%;
    }
  }

  &.yellow-second {
    animation-delay: 0.3s;
    left: 8%;
    top: 43%;
  }

  &.yellow-third {
    animation-delay: 0.4s;
    right: 8%;
    top: 70%;
    width: 70px;
    height: 70px;

    .bubble-chart.mobile & {
      right: 4%;
    }
  }
}

// 흐름 섹션 (경험 + 방향 통일)
.flow-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  .flow-wrapper.mobile & {
    gap: 8px;
  }
}

// 흐름 카드 (경험 + 방향 통일)
.flow-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: var(--flow-bg);

  // 색상 modifier
  &--blue {
    --flow-bg: #f3f7ff;
    --flow-tag-bg: rgba(87, 144, 252, 0.07);
    --flow-tag-color: #5790fc;
  }

  &--red {
    --flow-bg: #fff5f5;
    --flow-tag-bg: rgba(255, 110, 110, 0.07);
    --flow-tag-color: #ff6e6e;
  }

  &--yellow {
    --flow-bg: #fefbf3;
    --flow-tag-bg: rgba(243, 174, 17, 0.07);
    --flow-tag-color: #f3ae11;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__tag {
    padding: 3px 6px;
    border-radius: 4px;
    background: var(--flow-tag-bg);
    color: var(--flow-tag-color);
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
  }

  &__desc {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    padding: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);

    p {
      color: #333;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 400;
      line-height: 24px;
      margin: 0;
    }
  }

  &__recommend {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.8);
  }

  &__recommend-title {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
  }

  &__recommend-text {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }

  // 모바일 스타일
  .flow-wrapper.mobile & {
    border-radius: 0;
    margin-left: -16px;
    margin-right: -16px;
    padding: 16px;
  }
}

// 모바일 텍스트 크기
.flow-wrapper.mobile .flow-card__desc {
  font-size: 14px;
  line-height: 22px;
}

.flow-wrapper.mobile .flow-card__item p {
  font-size: 14px;
  line-height: 22px;
}

.flow-wrapper.mobile .flow-card__recommend-text {
  font-size: 14px;
  line-height: 22px;
}
</style>
